/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useRef, useState} from 'react'
import {FormattedMessage} from 'react-intl'
import {
    Box,
    Stack,
    Heading,
    Text,
    Grid,
    SimpleGrid,
    Checkbox,
    HStack,
    Flex,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalCloseButton,
    ModalBody,
    Button,
    IconButton,
    useDisclosure,
    useTheme,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {ShareIcon, CustomClose} from '../../../components/icons'
import {useProducts, useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'

import {useWishList} from '@salesforce/retail-react-app/app/hooks/use-wish-list'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import UnavailableProductConfirmationModal from '@salesforce/retail-react-app/app/components/unavailable-product-confirmation-modal'
import ProductTile, {Skeleton as ProductTileSkeleton} from '../../../components/product-tile'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {useWishlistActions} from '../../../hooks/use-wishlist-actions'
import {useCustomToast} from '../../../contexts/custom-toast-context'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useScript, setInitialScriptState} from '../../../contexts/script-context'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../../utils/site-config'
// TODO: refactor — legacy `optimizely/assets/recommendationsCarousel` removed in GDI cleanup, awaiting PDP/PLP refactor
const RecommendationsCarousel = () => null

const PRODUCTS_PER_PAGE = 24

const AccountWishlist = () => {
    const theme = useTheme()
    const isMobile = useBreakpointValue({ base: true, md: false })
    const {enableScript, disableScript} = useScript()
    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const siteId = site?.id
    if (getSiteEnvVarValue(appConfig, 'IS_NOIBU_DISABLED_WISHLIST', siteId) === 'true') {
        setInitialScriptState('noibu', false)
    }
    const headingRef = useRef()
    const {isOpen, onOpen, onClose} = useDisclosure()
    const appOrigin = useAppOrigin()

    // Pagination state
    const [page, setPage] = useState(1)
    const [allLoadedProducts, setAllLoadedProducts] = useState({})
    const [isLoadingMore, setIsLoadingMore] = useState(false)

    useEffect(() => {
        if (getSiteEnvVarValue(appConfig, 'IS_NOIBU_DISABLED_WISHLIST', siteId) === 'true') {
            disableScript('noibu')
        } else {
            enableScript('noibu')
        }

        // Focus the 'Wishlist' header when the component mounts for accessibility
        headingRef?.current?.focus()

        return () => {
            if (getSiteEnvVarValue(appConfig, 'IS_NOIBU_DISABLED_WISHLIST', siteId)) {
                enableScript('noibu')
            }
        }
    }, [])

    const {
        data: wishListData,
        isLoading: isWishListLoading,
        refetch: refetchWishList
    } = useWishList()
    useEffect(() => {
        refetchWishList?.()
    }, [refetchWishList])
    const productIds = wishListData?.customerProductListItems?.map((item) => item.productId)

    // Calculate which products to fetch for CURRENT page only (not all previous)
    const startIndex = (page - 1) * PRODUCTS_PER_PAGE
    const endIndex = page * PRODUCTS_PER_PAGE
    const currentPageProductIds = productIds?.slice(startIndex, endIndex)

    // Reset page and loaded products when wishlist changes significantly
    const totalWishlistItems = productIds?.length || 0
    useEffect(() => {
        if (totalWishlistItems === 0) {
            setPage(1)
            setAllLoadedProducts({})
        }
    }, [totalWishlistItems])

    const {data: productsData, isLoading: isProductsLoading} = useProducts(
        {
            parameters: {
                ids: currentPageProductIds?.join(','),
                allImages: true,
                perPricebook: true
            }
        },
        {enabled: currentPageProductIds?.length > 0}
    )

    // Accumulate products as they load
    useEffect(() => {
        if (productsData?.data) {
            setAllLoadedProducts((prev) => {
                const newProducts = {...prev}
                productsData.data.forEach((product) => {
                    newProducts[product.id] = product
                })
                return newProducts
            })
        }
    }, [productsData])

    // Calculate which products to display (all loaded so far)
    const displayedProductIds = productIds?.slice(0, endIndex)

    // If a product is added to the wishlist many times, it gets collapased into 1 line item
    const wishListItems = wishListData?.customerProductListItems
        ?.filter((item) => displayedProductIds?.includes(item.productId))
        ?.reduce((itemsData, item) => {
            const productData = allLoadedProducts[item.productId]
            return {...itemsData, [item.productId]: {...item, product: productData}}
        }, {})

    const deleteCustomerProductListItem = useShopperCustomersMutation(
        'deleteCustomerProductListItem'
    )

    const {data: customer} = useCurrentCustomer()
    const {toggleWishlistPublicStatus} = useWishlistActions()
    const {showCustomToast} = useCustomToast()

    const handleShareClick = async () => {
        if (!wishListData?.public) {
            onOpen()
        } else {
            // Copy link and show toast
            const wishlistUrl = `${appOrigin}/share-wishlist/${wishListData?.id}`
            await navigator.clipboard.writeText(wishlistUrl)

            // Show toast notification
            showCustomToast({
                message: 'Link copied'
            })
        }
    }

    const handleMakePublic = async () => {
        await toggleWishlistPublicStatus(true)
        onClose()
    }

    const handleUnavailableProducts = async (unavailableProductIds) => {
        if (!unavailableProductIds.length) return
        await Promise.all(
            unavailableProductIds.map(async (id) => {
                const item = wishListItems?.find((item) => {
                    return item.productId.toString() === id.toString()
                })
                const parameters = {
                    customerId: customer.customerId,
                    itemId: item?.id,
                    listId: wishListData?.id
                }
                await deleteCustomerProductListItem.mutateAsync({parameters})
            })
        )
    }

    const wishlistItemsLength = Object.keys(wishListItems ?? {}).length
    const hasWishlistItems = totalWishlistItems > 0
    const isInitialLoading = hasWishlistItems ? isProductsLoading && page === 1 : isWishListLoading
    const hasMoreItems = totalWishlistItems > endIndex

    const handleLoadMore = () => {
        setIsLoadingMore(true)
        setPage((prevPage) => prevPage + 1)
    }

    // Reset isLoadingMore when products finish loading
    useEffect(() => {
        if (!isProductsLoading && isLoadingMore) {
            setIsLoadingMore(false)
        }
    }, [isProductsLoading, isLoadingMore])

    return (
        <Stack spacing={{base: 6, lg: 8}} data-testid="account-wishlist-page">
            <Stack spacing={4}>
                <Flex justifyContent="space-between" alignItems="center">
                    <Heading
                        as="h1"
                        textTransform="uppercase"
                        fontSize={{base: '24px', lg: '32px'}}
                        tabIndex="0"
                        ref={headingRef}
                    >
                        {hasWishlistItems || isInitialLoading ? (
                            <FormattedMessage
                                defaultMessage="Wishlist"
                                id="account_wishlist.title.wishlist"
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="No items in your wishlist"
                                id="account_wishlist.empty.wishlist.title"
                            />
                        )}
                    </Heading>
                    {hasWishlistItems && !isInitialLoading && (
                        <IconButton
                            variant="unstyled"
                            h={'auto'}
                            minW={'auto'}
                            onClick={handleShareClick}
                            icon={<ShareIcon w="20px" h="20px" />}
                            aria-label="Open search modal"
                        />
                    )}
                </Flex>

                {hasWishlistItems && !isInitialLoading && (
                    <HStack spacing={3}>
                        <Checkbox
                            variant="filters"
                            isChecked={wishListData?.public || false}
                            onChange={(e) => toggleWishlistPublicStatus(e.target.checked)}
                        >
                            <Text fontSize="sm">
                                <FormattedMessage
                                    defaultMessage="Make my wishlist shareable"
                                    id="account_wishlist.shareable"
                                />
                            </Text>
                        </Checkbox>
                    </HStack>
                )}
            </Stack>

            {isInitialLoading && (
                <SimpleGrid columns={[2, 2, 3, 3]} spacingX={2} spacingY={{base: 6, lg: 8}}>
                    {new Array(PRODUCTS_PER_PAGE).fill(0).map((_, index) => (
                        <ProductTileSkeleton key={index} />
                    ))}
                </SimpleGrid>
            )}

            {!isInitialLoading && !hasWishlistItems && (
                <>
                    <Text>
                        <FormattedMessage
                            defaultMessage="We don't have any saved items to display for you, but here's some items you might like"
                            id="account_wishlist.empty.wishlist.info"
                        />
                    </Text>
                    <Box
                        mt={6}
                        mx={{base: -4, lg: 0}}
                        px={{base: 4, lg: 0}}
                        sx={{
                            // Mobile: allow carousel to bleed edge-to-edge
                            '& > div': {
                                mx: {base: -4, lg: 0}
                            },
                            '& > div > div:last-of-type': {
                                pl: {base: 4, lg: 0},
                                pr: {base: 4, lg: 0}
                            }
                        }}
                    >
                        <RecommendationsCarousel
                            Title="You May Also Like"
                            AIModel="recommendations"
                            carouselID="wishlist-recommendations"
                            TileNumber="2.3"
                            TabletTileNumber="3.5"
                            DesktopTileNumber="4"
                        />
                    </Box>
                </>
            )}

            {wishlistItemsLength >= 100 && (
                <Box bg="alternativeWarning.100" p={4} w="fit-content">
                    <Text color="warningText" fontSize="xs">
                        <FormattedMessage
                            defaultMessage="You have reached the maximum number of items for your Wishlist. Please remove some items before adding more."
                            id="account_wishlist.wishlist.error"
                        />
                    </Text>
                </Box>
            )}

            {!isInitialLoading && wishListItems && (
                <Stack>
                    <Grid templateColumns={{base: '1fr', md: '1fr'}} columnGap={6}>
                        <Box>
                            <SimpleGrid
                                columns={[2, 2, 3, 3]}
                                spacingX={2}
                                spacingY={{base: 6, lg: 8}}
                            >
                                {Object.keys(wishListItems).map((key) => {
                                    const item = wishListItems[key]
                                    const isOOS = item?.product?.inventory?.orderable === false

                                    return (
                                        <ProductTile
                                            key={`stl-tile-${key}`}
                                            data-testid="wishlist-product-tile"
                                            product={item?.product}
                                            enableFavourite={true}
                                            isFavourite={true}
                                            showSwatches={true}
                                            showVideo={true}
                                            dynamicImageProps={{
                                                widths: ['50vw', '50vw', '33vw', '33vw']
                                            }}
                                            isOOS={isOOS}
                                            isWishlistPage={true}
                                        />
                                    )
                                })}
                            </SimpleGrid>

                            {isLoadingMore && (
                                <SimpleGrid
                                    columns={[2, 2, 3, 3]}
                                    spacingX={2}
                                    spacingY={{base: 6, lg: 8}}
                                    mt={{base: 6, lg: 8}}
                                >
                                    {new Array(
                                        Math.max(
                                            0,
                                            Math.min(
                                                PRODUCTS_PER_PAGE,
                                                totalWishlistItems - endIndex
                                            )
                                        )
                                    )
                                        .fill(0)
                                        .map((_, index) => (
                                            <ProductTileSkeleton key={`loading-more-${index}`} />
                                        ))}
                                </SimpleGrid>
                            )}

                            {hasMoreItems && !isLoadingMore && (
                                <Flex justifyContent="center" gap={4} mt={10}>
                                    <Button
                                        variant="solid"
                                        onClick={handleLoadMore}
                                        width="100%"
                                        maxWidth={{base: '100%', lg: '300px'}}
                                        bg="black"
                                        color="white"
                                        fontSize="md"
                                        textTransform="uppercase"
                                    >
                                        <FormattedMessage
                                            defaultMessage="Show More"
                                            id="plp.button.show.more"
                                        />
                                    </Button>
                                </Flex>
                            )}
                        </Box>
                    </Grid>
                </Stack>
            )}

            <UnavailableProductConfirmationModal
                productItems={wishListData?.customerProductListItems}
                handleUnavailableProducts={handleUnavailableProducts}
            />

            <Modal isOpen={isOpen} onClose={onClose} isCentered>
                <ModalOverlay />
                <ModalContent mb={{base: 0, lg: 'auto'}}>
                    <ModalCloseButton>
                        <CustomClose />
                    </ModalCloseButton>
                    <ModalBody p="23px">
                        <Text fontSize="sm" mt={6} mb="30px">
                            <FormattedMessage
                                defaultMessage="Your wishlist is private. Make it public so you can share it with anyone you like."
                                id="wishlist.modal.share.description"
                            />
                        </Text>
                        <Button w="full" mx="auto" onClick={handleMakePublic} fontSize="md">
                            <FormattedMessage
                                defaultMessage="Make Wishlist Shareable"
                                id="wishlist.modal.make_shareable"
                            />
                        </Button>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Stack>
    )
}

export default AccountWishlist
